import React, { useEffect, useState, useCallback } from "react";
import CompanyCard from "./MiniComponents/CompanyCard/CompanyCard";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import {
  getAllCompanies,
  searchCompanies,
} from "../../../redux/companySlice/companySlice";
import "./Companies.css";

export default function Companies() {
  const dispatch = useDispatch();
  const { allCompanies, searchResults, status, searchStatus } = useSelector(
    (state) => state.company,
  );

  // Local State
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((keyword) => {
      if (keyword.trim() !== "") {
        setIsSearching(true);
        dispatch(searchCompanies({ keyword: keyword.trim(), page: 1 }));
        setCurrentPage(1);
      } else {
        setIsSearching(false);
        dispatch(getAllCompanies(1));
        setCurrentPage(1);
      }
    }, 500),
    [dispatch],
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery("");
    setIsSearching(false);
    setCurrentPage(1);
    dispatch(getAllCompanies(1));
  };

  // جلب البيانات عند تحميل الصفحة
  useEffect(() => {
    if (!isSearching) {
      dispatch(getAllCompanies(currentPage));
    }
  }, [dispatch, currentPage, isSearching]);

  // تحديد المصدر الصحيح للبيانات
  const dataSource = isSearching ? searchResults : allCompanies;

  // استخراج البيانات بشكل آمن
  const companies = Array.isArray(dataSource?.data) ? dataSource.data : [];
  const pagination = dataSource?.pagination || null;
  const totalCompanies = pagination?.total || 0;

  // حساب عدد الصفحات بشكل آمن
  const calculateTotalPages = () => {
    if (!pagination?.total || !pagination?.limit) return 1;

    const total = Number(pagination.total);
    const limit = Number(pagination.limit);

    if (!Number.isFinite(total) || !Number.isFinite(limit) || limit <= 0) {
      return 1;
    }

    const pages = Math.ceil(total / limit);
    return Math.max(1, Math.min(100, pages));
  };

  const totalPages = calculateTotalPages();

  // Handle pagination
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;

    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });

    if (isSearching) {
      dispatch(searchCompanies({ keyword: searchQuery.trim(), page }));
    } else {
      dispatch(getAllCompanies(page));
    }
  };

  const loading = status === "loading" || searchStatus === "loading";
  const showPagination = pagination && totalPages > 1;

  return (
    <>
      <div className="main">
        <section>
          <div>
            <h1 className="text-white">الشركات</h1>
          </div>
          <div className="container-fluid">
            <div className="row justify-content-between align-items-center mb-5 mt-3">
              {/* عدد الشركات */}
              <div className="col-md-3 d-flex justify-content-md-start justify-content-center mb-3">
                <div className="card py-2 px-3">
                  <p className="mb-0 primary-100">
                    عدد الشركات:{" "}
                    <span className="primary-100">{totalCompanies}</span>
                  </p>
                </div>
              </div>

              {/* Search Box */}
              <div className="col-md-5 mb-3">
                <div className="search-box-container position-relative">
                  <i className="fas fa-search search-icon-companies"></i>
                  <input
                    type="search"
                    placeholder="ابحث بالاسم، السجل التجاري، الرقم الضريبي، الهاتف..."
                    className="form-control search-input-companies"
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                  {searchQuery && (
                    <button
                      className="btn-clear-search-companies"
                      onClick={clearSearch}
                      title="مسح البحث"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
                {isSearching && (
                  <div className="search-info-companies text-center mt-2">
                    <small className="text-muted">
                      <i className="fas fa-search me-1"></i>
                      البحث عن: "{searchQuery}"
                    </small>
                  </div>
                )}
              </div>

              {/* زر إضافة شركة */}
              <div className="col-md-3 text-md-end text-center mb-3">
                <Link className="btn btn-primary text-white" to={"/AddCompany"}>
                  <i className="fas fa-plus me-2"></i>
                  إضافة شركة
                </Link>
              </div>
            </div>

            {/* Loading Spinner */}
            {loading ? (
              <div className="loading-spinner-companies">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">جاري التحميل...</span>
                </div>
              </div>
            ) : (
              <>
                {/* Companies Grid */}
                <div className="companies row align-items-stretch">
                  {companies.length > 0 ? (
                    companies.map((company) => (
                      <CompanyCard key={company.id} company={company} />
                    ))
                  ) : (
                    <div className="col-12 text-center py-5">
                      <i className="fas fa-building fa-3x text-muted mb-3"></i>
                      <p className="text-muted">
                        {searchQuery ? "لا توجد نتائج للبحث" : "لا توجد شركات"}
                      </p>
                      {searchQuery && (
                        <button
                          className="btn btn-sm btn-primary mt-2"
                          onClick={clearSearch}
                        >
                          <i className="fas fa-times me-2"></i>
                          مسح البحث
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Pagination */}
                {showPagination && (
                  <div className="pagination-section-companies mt-4">
                    <nav aria-label="Company pagination">
                      <ul className="pagination justify-content-center">
                        <li
                          className={`page-item ${
                            currentPage === 1 ? "disabled" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            aria-label="الصفحة السابقة"
                          >
                            السابق
                          </button>
                        </li>

                        {Array.from({ length: totalPages }, (_, index) => {
                          const pageNum = index + 1;
                          return (
                            <li
                              key={pageNum}
                              className={`page-item ${
                                currentPage === pageNum ? "active" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(pageNum)}
                                aria-label={`الصفحة ${pageNum}`}
                                aria-current={
                                  currentPage === pageNum ? "page" : undefined
                                }
                              >
                                {pageNum}
                              </button>
                            </li>
                          );
                        })}

                        <li
                          className={`page-item ${
                            currentPage === totalPages ? "disabled" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            aria-label="الصفحة التالية"
                          >
                            التالي
                          </button>
                        </li>
                      </ul>
                    </nav>

                    <div className="pagination-info-companies text-center mt-2">
                      <small className="text-muted">
                        الصفحة {currentPage} من {totalPages}
                      </small>
                    </div>
                  </div>
                )}

                {/* Results Summary */}
                {companies.length > 0 && (
                  <div className="results-summary-companies text-center mt-3">
                    <p className="text-muted">
                      عرض {companies.length} من {totalCompanies} شركة
                      {searchQuery && (
                        <span className="search-indicator-companies">
                          {" "}
                          - نتائج البحث عن: "{searchQuery}"
                        </span>
                      )}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>
    </>
  );
}
